module GraphQL.Type where

import Prelude

import Data.Argonaut (class DecodeJson, class EncodeJson, decodeJson, encodeJson)
import Data.Maybe (Maybe(..))
import Data.Symbol (class IsSymbol, reflectSymbol)
import Type.Prelude (Proxy(..))

-- | Defines the type of a GraphQL value
data QType = QType String | Nullable QType | ArrayOf QType

typeToString :: QType -> String
typeToString (Nullable (QType t)) = t
typeToString (QType t) = t <> "!"
typeToString (Nullable (ArrayOf t)) = "[" <> typeToString t <> "]"
typeToString (ArrayOf t) = "[" <> typeToString t <> "]!"
-- The below case should never happen, but in the event it does we simply
-- ignore the outer `Nullable` and return the inner type.
typeToString (Nullable t@(Nullable _)) = typeToString t


-- | Class for types which correspond to a named GraphQL type
class HasQType :: forall k. k -> Constraint
class HasQType a where
    gqlType :: Proxy a -> QType


instance hqtMaybe :: HasQType a => HasQType (Maybe a) where
    gqlType _ = Nullable $ gqlType (Proxy :: Proxy a)

instance hqtArray :: HasQType a => HasQType (Array a) where
    gqlType _ = ArrayOf $ gqlType (Proxy :: Proxy a)

instance hqtStr :: HasQType String where
    gqlType _ = QType "String"
instance hqtInt :: HasQType Int where
    gqlType _ = QType "Int"
instance hqtFloat :: HasQType Number where
    gqlType _ = QType "Float"
instance hqtBoolean :: HasQType Boolean where
    gqlType _ = QType "Boolean"

-- Wrapper for GraphQL ID types
data QID = QID String
instance hqtID :: HasQType QID where
    gqlType _ = QType "ID"
instance encQID :: EncodeJson QID where
    encodeJson (QID id) = encodeJson id

-- | Wrapper for strongly typed GraphQL IDs
-- |
-- | These are checked at the type level to ensure that different types of ID
-- | have different types. In GraphQL they are all given the `ID` type.
-- |
-- | Example:
-- | ```
-- | type UserID = TypedID "User"
-- | ```
-- | 
-- | Construct with `TypedID "0"`, unwrap with `innerId`
type TypedID :: Symbol -> Type
type TypedID sym = RenamedType sym "ID" String

typedId :: forall sym. String -> TypedID sym
typedId = RenamedType

-- | Get the inner string for this ID
innerId :: forall sym. TypedID sym -> String
innerId (RenamedType s) = s

dummyId :: forall sym. TypedID sym
dummyId = RenamedType "-1"

maybeId :: forall sym. Maybe (TypedID sym) -> TypedID sym
maybeId Nothing = dummyId
maybeId (Just id) = id


-- | Similar to `TypedID` but uses `String` as its GraphQL type
type TypedString :: Symbol -> Type
type TypedString sym = RenamedType sym "String" String

typedStr :: forall sym. String -> TypedString sym
typedStr = RenamedType

innerStr :: forall sym. TypedString sym -> String
innerStr (RenamedType s) = s


-- | Data wrapper which overrides the GraphQL type name used for the inner type
type NamedType sym a = RenamedType sym sym a

namedType :: forall sym a. a -> NamedType sym a
namedType = RenamedType


-- | The basis for `TypedID`, `TypedString`, and `NamedType`
-- |
-- | This allows a purescript type to be wrapped in a marker data type which
-- | both separates values of the same purescript type by symbol `psym` (as
-- | used in `TypedID`) and allows the GraphQL type name to be specified by
-- | symbol (as used in `NamedType`).
-- |
-- | You probably want to use one of the above type aliases rather than this
-- | directly.
data RenamedType :: Symbol -> Symbol -> Type -> Type
data RenamedType psym gsym a = RenamedType a

innerType :: forall psym gsym a. RenamedType psym gsym a -> a
innerType (RenamedType a) = a

instance rntyType :: IsSymbol gsym => HasQType (RenamedType psym gsym t) where
    gqlType _ = QType $ reflectSymbol (Proxy :: Proxy gsym)

instance encRNTY :: EncodeJson t => EncodeJson (RenamedType psym gsym t) where
    encodeJson (RenamedType id) = encodeJson id
instance decRNTY :: DecodeJson t => DecodeJson (RenamedType psym gsym t) where
    decodeJson json = RenamedType <$> decodeJson json

instance eqRNTY :: Eq t => Eq (RenamedType psym gsym t) where
    eq (RenamedType a) (RenamedType b) = a == b
instance ordRNTY :: Ord t => Ord (RenamedType psym gsym t) where
    compare (RenamedType a) (RenamedType b) = compare a b


-- | Data wrapper which overrides the GraphQL type name used for the inner type
data DyNamedType a = DyNamedType String a

instance encDYNT :: EncodeJson t => EncodeJson (DyNamedType t) where
    encodeJson (DyNamedType _ id) = encodeJson id

instance eqDYNT :: Eq t => Eq (DyNamedType t) where
    eq (DyNamedType _ a) (DyNamedType _ b) = a == b
instance ordDYNT :: Ord t => Ord (DyNamedType t) where
    compare (DyNamedType _ a) (DyNamedType _ b) = compare a b
